import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Avatar,
  Rating,
  IconButton
} from '@mui/material';
import {
  RateReview as RateReviewIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useToast } from '../ToastProvider/ToastProvider';

interface SubmitReviewModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (review: ReviewData) => Promise<void>;
  userId?: string;
}

interface ReviewData {
  rating: number;
  comment: string;
}

export default function SubmitReviewModal({
  open,
  onClose,
  onSubmit,
  userId
}: SubmitReviewModalProps) {
  const toast = useToast();
  
  const [step, setStep] = useState(0); // 0: Form, 1: Success
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState<ReviewData>({
    rating: 0,
    comment: ''
  });

  const handleChange = (field: keyof ReviewData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    // If closing without submitting (not on success screen), save dismissal to sessionStorage
    if (step === 0 && userId) {
      sessionStorage.setItem(`reviewModalDismissed_${userId}`, 'true');
    }
    setFormData({
      rating: 0,
      comment: ''
    });
    setStep(0);
    onClose();
  };

  const validateForm = () => {
    if (formData.rating === 0) {
      toast.showError('Please provide a rating');
      return false;
    }

    if (!formData.comment.trim()) {
      toast.showError('Please provide a comment');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      toast.showSuccess('Thank you for your review!');
      setStep(1);
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error: any) {
      toast.showError(error.message || 'Failed to submit review. Please try again.');
      console.error('Error submitting review:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRatingLabel = (rating: number) => {
    switch (rating) {
      case 1: return 'Poor';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: return 'Very Good';
      case 5: return 'Excellent';
      default: return '';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <RateReviewIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {step === 0 ? 'Share your feedback' : 'Thank You!'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {step === 0 ? 'Please take the time to share your experience with us' : 'Review submitted successfully'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ ml: 2 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Form Step */}
        {step === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* Rating */}
            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                How would you rate Stafflows? *
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Rating
                  value={formData.rating}
                  onChange={(e, value) => handleChange('rating', value || 0)}
                  size="large"
                  icon={<StarIcon fontSize="inherit" />}
                />
                {formData.rating > 0 && (
                  <Typography variant="body2" fontWeight={600} color="primary">
                    {getRatingLabel(formData.rating)}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Comment */}
            <TextField
              fullWidth
              label="Your Comment"
              value={formData.comment}
              onChange={(e) => handleChange('comment', e.target.value)}
              multiline
              rows={4}
              placeholder="Tell us what you think about Stafflows..."
              required
            />
          </Box>
        )}

        {/* Success Step */}
        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
            <Avatar sx={{ bgcolor: 'success.light', width: 60, height: 60 }}>
              <CheckCircleIcon sx={{ fontSize: 36, color: 'success.main' }} />
            </Avatar>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Thank you for your review! Your feedback helps us improve.
            </Typography>
          </Box>
        )}
      </DialogContent>

      {step === 0 && (
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={handleClose} variant="outlined" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      )}

      {step === 1 && (
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={handleClose} variant="contained" fullWidth>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

// Example usage
export function SubmitReviewExample() {
  const [showModal, setShowModal] = useState(false);

  const handleSubmitReview = async (review: ReviewData) => {
    // TODO: Replace with actual API call
    console.log('Submitting review:', review);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));
  };

  return (
    <Box sx={{ p: 4 }}>
      <Button 
        variant="contained" 
        startIcon={<RateReviewIcon />}
        onClick={() => setShowModal(true)}
      >
        Leave a Review
      </Button>

      <SubmitReviewModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmitReview}
      />
    </Box>
  );
}